import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

const DailyDetectionChart = () => {
  const [rawData, setRawData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!supabase) {
        throw new Error('Supabase client not configured.');
      }

      const pageSize = 1000;
      let from = 0;
      let allData = [];
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('detection')
          .select(`
            absolute_start_time,
            confidence,
            species (
              common_name
            )
          `)
          .gte('confidence', 0.1)
          .order('absolute_start_time')
          .range(from, from + pageSize - 1);

        if (error) throw error;

        allData = allData.concat(data || []);
        hasMore = data && data.length === pageSize;
        from += pageSize;
      }

      setRawData(allData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const processDataForChart = useCallback((data, startDate, endDate) => {
    if (!data || data.length === 0) return [];

    const rangeData = data.filter(detection => {
      const dateStr = detection.absolute_start_time.split('T')[0];
      if (!dateStr) return false;
      return dateStr >= startDate && dateStr <= endDate;
    });

    if (rangeData.length === 0) return [];

    const dailyCounts = {};

    rangeData.forEach(detection => {
      const dateStr = detection.absolute_start_time.split('T')[0];
      dailyCounts[dateStr] = (dailyCounts[dateStr] || 0) + 1;
    });

    const result = Object.entries(dailyCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateStr, count]) => ({
        date: dateStr,
        displayDate: new Date(dateStr).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }),
        detections: count
      }));

    return result;
  }, []);


  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (rawData.length > 0) {
      const processedData = processDataForChart(rawData, dateRange.start, dateRange.end);
      setChartData(processedData);
    }
  }, [rawData, dateRange, processDataForChart]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="h6">Loading detection data...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error">Error loading data: {error}</Alert>
      </Box>
    );
  }

  return (
    <Card elevation={3} sx={{ width: '100%', mx: 'auto' }}>
      <CardContent sx={{ p: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Typography variant="h5" component="h2" fontWeight="medium">
            📊 Daily Detection Count
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              type="date"
              label="Start Date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              slotProps={{
                inputLabel: { shrink: true }
              }}
              size="small"
              sx={{ minWidth: 160 }}
            />
            <TextField
              type="date"
              label="End Date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              slotProps={{
                inputLabel: { shrink: true }
              }}
              size="small"
              sx={{ minWidth: 160 }}
            />
          </Stack>
        </Stack>
        
        <Box sx={{ width: '100%', height: 400 }}>
          {chartData.length === 0 ? (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              height="100%"
            >
              <Typography variant="body1" color="text.secondary">
                No detections found for the selected date range
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="displayDate" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Detections', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  formatter={(value) => [value, 'Detections']}
                  labelStyle={{ color: '#333' }}
                />
                <Line 
                  type="monotone"
                  dataKey="detections" 
                  stroke="#1976d2"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default DailyDetectionChart;
